<template>
  <form @submit.prevent="submit">
    <b-card v-if="loaded">
      <fields :form="form" ungrouped />
    </b-card>

    <b-card v-if="loaded && hasVehicleRows" :title="$t('vehicles.zone_to_zone_vehicles')">
      <div class="table-responsive">
        <table class="table no-footer">
          <thead>
            <tr>
              <th>{{ $t('bookings.vehicle_class') }}</th>
              <th>{{ $t('vehicles.availability') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in divisionVehicles" :key="v.id">
              <td>{{ v.name }}</td>
              <td>
                <b-form-select
                  v-model="vehicleAvailabilityMap[v.id]"
                  :options="availabilitySelectOptions"
                  size="sm"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <b-button type="submit" variant="primary">
      {{ $t('general.save') }}
    </b-button>
    <b-button type="button" variant="light" class="ml-2" @click="cancel">
      {{ $t('general.cancel') }}
    </b-button>
  </form>
</template>

<script>
import isPlainObject from 'lodash-es/isPlainObject'
import { mapState } from 'vuex'
import { ResourceFormMixin } from 'stylemix-vue-admin'

export default {
  name: 'VehicleAvailabilityRuleForm',

  mixins: [ResourceFormMixin],

  props: {
    id: { type: [Number, String], required: false, default: null },
    editId: { type: [Number, String], required: false, default: null },
    apiInterface: { type: Object, required: true },
  },

  data() {
    return {
      loaded: false,
      vehicleAvailabilityMap: {},
    }
  },

  computed: {
    ...mapState('zones', ['currentDivision']),
    form() {
      return this
    },
    effectiveId() {
      return this.id ?? this.editId
    },
    divisionVehicles() {
      const list = this.model.vehicles
      return Array.isArray(list) ? list : []
    },
    hasVehicleRows() {
      return this.divisionVehicles.length > 0
    },
    availabilitySelectOptions() {
      return ['allowed', 'only_pickup', 'only_dropoff', 'forbidden'].map(value => ({
        value,
        text: this.$t(`vehicles.price_zone_condition.${value}`),
      }))
    },
  },

  watch: {
    effectiveId() {
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    api() {
      return this.apiInterface
    },

    initVehicleAvailabilityMap() {
      const next = {}
      const rawAvail = this.model.vehicle_availability
      const fromModel = isPlainObject(rawAvail) ? rawAvail : {}
      for (const v of this.divisionVehicles) {
        const fromFlat = fromModel[String(v.id)] ?? fromModel[v.id]
        const fromPivot =
          v.pivot && v.pivot.availability != null && v.pivot.availability !== ''
            ? v.pivot.availability
            : null
        const raw = fromFlat ?? fromPivot ?? 'allowed'
        this.$set(next, v.id, typeof raw === 'string' ? raw : String(raw))
      }
      this.vehicleAvailabilityMap = next
    },

    serializeVehicleAvailability() {
      const out = {}
      for (const v of this.divisionVehicles) {
        out[String(v.id)] = this.vehicleAvailabilityMap[v.id] ?? 'allowed'
      }
      return out
    },

    submit() {
      this.$set(this.model, 'vehicle_availability', this.serializeVehicleAvailability())
      this.save()
        .then(result => {
          this.$toast.success(result.message)
        })
        .catch(() => {})
    },

    cancel() {
      this.$emit('cancel')
    },

    load() {
      this.loaded = false
      this.loadFormWithEffectiveId().then(() => {
        this.loaded = true
        this.initVehicleAvailabilityMap()
      })
    },

    loadFormWithEffectiveId() {
      const effectiveId = this.effectiveId
      const promise = effectiveId ? this.api().edit(effectiveId) : this.api().create()
      const fullPromise = promise.then(result => {
        this.model = isPlainObject(result.data) ? result.data : {}
        this.setFields(result.fields)
        if (!this.effectiveId && this.currentDivision?.id) {
          this.$set(this.model, 'division_id', this.currentDivision.id)
        }
        return result
      })
      return this.$uiBlocker(fullPromise, this.$el)
    },

    save() {
      this.errors.clear()
      if (!this.effectiveId && this.currentDivision?.id) {
        this.$set(this.model, 'division_id', this.currentDivision.id)
      }
      this.$set(this.model, 'vehicle_availability', this.serializeVehicleAvailability())
      const formData = this.formData(this.model)
      const effectiveId = this.effectiveId
      const promise = effectiveId
        ? this.api().update(effectiveId, formData)
        : this.api().store(formData)
      const fullPromise = promise
        .then(result => {
          this.onSaved(result)
          return result
        })
        .catch(response => {
          this.onSaveError(response)
          return Promise.reject(response)
        })
      return this.$uiBlocker(fullPromise, this.$el)
    },

    onSaved(result) {
      this.$emit('saved', result)
    },
  },
}
</script>
